import React, { useState, useEffect, useMemo } from 'react';
import { 
    Users, UserPlus, Edit, Trash2, Shield, Phone, 
    Key, Save, X, Loader2, ChevronLeft, ChevronRight, LogOut, UserMinus, AlertTriangle 
} from 'lucide-react';
import api from '../services/api';

const AdminUsuarios = ({ showToast }) => {
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Estado do Modal de Cadastro
    const [modalAberto, setModalAberto] = useState(false);
    const [editando, setEditando] = useState(null);
    const [form, setForm] = useState({ nome: '', telefone: '', senha: '', perfil: 'tecnico' });

    // Estado do Modal de Confirmação (Substituindo o window.confirm)
    const [modalConfirmacao, setModalConfirmacao] = useState({
        aberto: false,
        titulo: '',
        mensagem: '',
        textoBotao: '',
        corBotao: '',
        acao: null
    });
    
    // Paginação
    const [paginaAtual, setPaginaAtual] = useState(1);
    const itensPorPagina = 5;

    useEffect(() => {
        carregarUsuarios();
    }, []);

    const carregarUsuarios = async () => {
        setLoading(true);
        try {
            const res = await api.get('/usuarios');
            setUsuarios(res.data);
        } catch (err) {
            showToast("Erro ao carregar usuários", "error");
        } finally {
            setLoading(false);
        }
    };

    // --- LÓGICA DO MODAL DE CADASTRO ---
    const abrirModal = (usuario = null) => {
        if (usuario) {
            setEditando(usuario.id);
            setForm({ nome: usuario.nome, telefone: usuario.telefone, perfil: usuario.perfil, senha: '' });
        } else {
            setEditando(null);
            setForm({ nome: '', telefone: '', senha: '', perfil: 'tecnico' });
        }
        setModalAberto(true);
    };

    const fecharModal = () => {
        setModalAberto(false);
        setEditando(null);
        setForm({ nome: '', telefone: '', senha: '', perfil: 'tecnico' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editando) {
                await api.put(`/usuarios/${editando}`, form);
                showToast("Usuário atualizado!");
            } else {
                await api.post('/usuarios', form);
                showToast("Usuário criado com sucesso!");
            }
            fecharModal();
            carregarUsuarios();
        } catch (err) {
            showToast("Erro ao salvar usuário", "error");
        }
    };

    // --- LÓGICA DO MODAL DE CONFIRMAÇÃO ---
    const fecharModalConfirmacao = () => {
        setModalConfirmacao({ ...modalConfirmacao, aberto: false });
    };

    const deletarUsuario = (id) => {
        setModalConfirmacao({
            aberto: true,
            titulo: 'Remover Acesso',
            mensagem: 'Tem certeza que deseja remover este acesso permanentemente? Esta ação não pode ser desfeita.',
            textoBotao: 'Sim, Remover',
            corBotao: 'bg-red-600 hover:bg-red-500 shadow-red-500/20',
            acao: async () => {
                fecharModalConfirmacao();
                try {
                    await api.delete(`/usuarios/${id}`);
                    showToast("Usuário removido");
                    carregarUsuarios();
                } catch (err) {
                    showToast("Erro ao excluir", "error");
                }
            }
        });
    };

    const deslogarUsuario = (id, nome) => {
        setModalConfirmacao({
            aberto: true,
            titulo: 'Desconectar Usuário',
            mensagem: `Deseja realmente desconectar o usuário ${nome}? Ele precisará fazer login novamente para acessar o sistema.`,
            textoBotao: 'Desconectar',
            corBotao: 'bg-orange-500 hover:bg-orange-400 shadow-orange-500/20',
            acao: async () => {
                fecharModalConfirmacao();
                try {
                    setLoading(true);
                    await api.post(`/usuarios/${id}/logout`); 
                    showToast(`${nome} foi desconectado com sucesso!`);
                } catch (err) {
                    if(err.response?.status === 404) {
                        showToast("Rota de logout individual não encontrada na API (404).", "error");
                    } else {
                        showToast("Erro ao tentar deslogar o usuário.", "error");
                    }
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const deslogarTodosUsuarios = () => {
        setModalConfirmacao({
            aberto: true,
            titulo: 'Deslogar Todos os Usuários',
            mensagem: 'ATENÇÃO: Você está prestes a desconectar todos os outros usuários ativos do sistema. Tem certeza que deseja continuar?',
            textoBotao: 'Deslogar Todos',
            corBotao: 'bg-red-600 hover:bg-red-500 shadow-red-500/20',
            acao: async () => {
                fecharModalConfirmacao();
                try {
                    setLoading(true);
                    await api.post('/usuarios/logout-all'); 
                    showToast("Comando de logout geral enviado!");
                } catch (err) {
                    if(err.response?.status === 404) {
                        showToast("Rota de logout geral não encontrada na API (404).", "error");
                    } else {
                        showToast("Erro ao tentar deslogar todos.", "error");
                    }
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // Lógica de Paginação
    const totalPaginas = Math.ceil(usuarios.length / itensPorPagina);
    const usuariosExibidos = useMemo(() => {
        const inicio = (paginaAtual - 1) * itensPorPagina;
        return usuarios.slice(inicio, inicio + itensPorPagina);
    }, [usuarios, paginaAtual]);

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header com Botões de Ação */}
            <div className="bg-slate-900 p-8 rounded-[40px] border border-slate-800 flex flex-col md:flex-row justify-between items-center shadow-2xl gap-4">
                <div>
                    <h2 className="text-2xl font-black text-white flex items-center gap-3 tracking-tighter">
                        <Users className="text-emerald-500" /> Gestão de Equipe
                    </h2>
                    <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-1">Controle de Acessos CTC-IT</p>
                </div>
                
                <div className="flex gap-3">
                    <button 
                        onClick={deslogarTodosUsuarios}
                        className="bg-red-600/10 hover:bg-red-600 text-red-500 hover:text-white px-6 py-4 rounded-3xl font-black text-[10px] uppercase flex items-center gap-2 transition-all shadow-lg hover:shadow-red-500/20 active:scale-95 border border-red-500/20"
                    >
                        <LogOut size={18} /> Deslogar Todos
                    </button>
                    
                    <button 
                        onClick={() => abrirModal()}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-4 rounded-3xl font-black text-[10px] uppercase flex items-center gap-2 transition-all shadow-lg shadow-emerald-500/20 active:scale-95"
                    >
                        <UserPlus size={18} /> Novo Usuário
                    </button>
                </div>
            </div>

            {/* Modal de Confirmação Genérico */}
            {modalConfirmacao.aberto && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[40px] border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 p-8 text-center space-y-6">
                        <div className="mx-auto w-20 h-20 bg-slate-50 dark:bg-slate-800/50 rounded-full flex items-center justify-center mb-4">
                            <AlertTriangle className="text-slate-400" size={36} />
                        </div>
                        <div>
                            <h3 className="text-xl font-black dark:text-white uppercase tracking-tighter mb-3">
                                {modalConfirmacao.titulo}
                            </h3>
                            <p className="text-sm font-medium text-slate-500 leading-relaxed">
                                {modalConfirmacao.mensagem}
                            </p>
                        </div>
                        <div className="flex justify-center gap-3 pt-4">
                            <button onClick={fecharModalConfirmacao} 
                                    className="px-6 py-4 rounded-3xl font-black text-[10px] uppercase bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all">
                                Cancelar
                            </button>
                            <button onClick={modalConfirmacao.acao} 
                                    className={`px-8 py-4 rounded-3xl font-black text-[10px] uppercase text-white flex items-center gap-2 shadow-lg active:scale-95 transition-all ${modalConfirmacao.corBotao}`}>
                                {modalConfirmacao.textoBotao}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Cadastro/Edição */}
            {modalAberto && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-[40px] border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="p-8 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
                            <h3 className="text-xl font-black dark:text-white uppercase tracking-tighter">
                                {editando ? 'Editar Usuário' : 'Novo Cadastro'}
                            </h3>
                            <button onClick={fecharModal} className="p-2 text-slate-400 hover:text-red-500 transition-colors">
                                <X size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-8 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-500 uppercase ml-4 tracking-widest">Nome Completo</label>
                                    <input className="w-full bg-slate-50 dark:bg-slate-950 border-2 border-slate-100 dark:border-slate-800 p-4 rounded-3xl outline-none focus:border-emerald-500 dark:text-white font-bold transition-all" 
                                           value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} required />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-500 uppercase ml-4 tracking-widest">Telefone (Login)</label>
                                    <input className="w-full bg-slate-50 dark:bg-slate-950 border-2 border-slate-100 dark:border-slate-800 p-4 rounded-3xl outline-none focus:border-emerald-500 dark:text-white font-bold transition-all" 
                                           value={form.telefone} onChange={e => setForm({...form, telefone: e.target.value})} required />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-500 uppercase ml-4 tracking-widest">{editando ? 'Nova Senha (Opcional)' : 'Senha Digital'}</label>
                                    <input type="password" className="w-full bg-slate-50 dark:bg-slate-950 border-2 border-slate-100 dark:border-slate-800 p-4 rounded-3xl outline-none focus:border-emerald-500 dark:text-white font-bold transition-all" 
                                           value={form.senha} onChange={e => setForm({...form, senha: e.target.value})} required={!editando} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-500 uppercase ml-4 tracking-widest">Nível de Acesso</label>
                                    <select className="w-full bg-slate-50 dark:bg-slate-950 border-2 border-slate-100 dark:border-slate-800 p-4 rounded-3xl outline-none focus:border-emerald-500 dark:text-white font-bold appearance-none transition-all"
                                            value={form.perfil} onChange={e => setForm({...form, perfil: e.target.value})}>
                                        <option value="tecnico">Técnico de Campo</option>
                                        <option value="admin">Administrador</option>
                                        <option value="superadmin">SuperAdmin</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <button type="button" onClick={fecharModal} 
                                        className="px-8 py-4 rounded-3xl font-black text-[10px] uppercase bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all">
                                    Cancelar
                                </button>
                                <button type="submit" 
                                        className="px-10 py-4 rounded-3xl font-black text-[10px] uppercase bg-emerald-600 text-white flex items-center gap-2 shadow-lg shadow-emerald-500/20 active:scale-95 transition-all">
                                    <Save size={16} /> {editando ? 'Salvar Alterações' : 'Confirmar Cadastro'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Tabela de Usuários */}
            <div className="bg-white dark:bg-slate-900 rounded-[40px] border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50 dark:bg-slate-800/50">
                            <tr>
                                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Usuário</th>
                                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Acesso</th>
                                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Perfil</th>
                                <th className="p-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {usuariosExibidos.map(u => (
                                <tr key={u.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all group">
                                    <td className="p-6">
                                        <div className="font-black text-sm dark:text-white uppercase tracking-tighter italic">{u.nome}</div>
                                        <div className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                                            Último Login: {u.ultimo_login ? new Date(u.ultimo_login).toLocaleString() : 'Nunca'}
                                        </div>
                                    </td>
                                    <td className="p-6 font-mono text-xs font-bold text-slate-500">{u.telefone}</td>
                                    <td className="p-6 text-center">
                                        <span className={`px-4 py-1 rounded-full text-[9px] font-black uppercase tracking-tighter border 
                                            ${u.perfil === 'superadmin' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 
                                              u.perfil === 'admin' ? 'bg-purple-500/10 text-purple-500 border-purple-500/20' : 
                                              'bg-blue-500/10 text-blue-500 border-blue-500/20'}`}>
                                            {u.perfil}
                                        </span>
                                    </td>
                                    <td className="p-6 text-right space-x-2">
                                        <button onClick={() => deslogarUsuario(u.id, u.nome)} 
                                                title="Deslogar este usuário"
                                                className="p-3 bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-orange-500 rounded-xl transition-all shadow-sm">
                                            <UserMinus size={16} />
                                        </button>
                                        <button onClick={() => abrirModal(u)} 
                                                title="Editar usuário"
                                                className="p-3 bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-emerald-500 rounded-xl transition-all shadow-sm">
                                            <Edit size={16} />
                                        </button>
                                        <button onClick={() => deletarUsuario(u.id)} 
                                                title="Excluir usuário"
                                                className="p-3 bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-red-500 rounded-xl transition-all shadow-sm">
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Controles de Paginação */}
                <div className="p-6 bg-slate-50/50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        Página {paginaAtual} de {totalPaginas}
                    </p>
                    <div className="flex gap-2">
                        <button 
                            disabled={paginaAtual === 1}
                            onClick={() => setPaginaAtual(prev => prev - 1)}
                            className="p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-500 disabled:opacity-30 transition-all hover:border-emerald-500"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <button 
                            disabled={paginaAtual === totalPaginas}
                            onClick={() => setPaginaAtual(prev => prev + 1)}
                            className="p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-500 disabled:opacity-30 transition-all hover:border-emerald-500"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminUsuarios;